import { object, string, bool } from "yup";
import { useFormik } from "formik";
import { useModalHooks } from "../../../../../../App/State/Hooks/useModalHooks";
import { useNotification } from "../../../../../../App/State/Hooks/useNotificationHooks";
import { ICreateFeature } from "../../../../../../App/Typings/AbacFeature";
import { useCreateAbacAppFeatureMutation } from "../../../../AbacAppQueries";
import { useCallback } from "react";

export default function useCreateFeatureFormState(appId: string) {
  const createFeatureSuccessMessage = "New feature created!";
  const createFeatureErrorMessage = "Error creating new Feature.";
  const { closeModal } = useModalHooks();
  const { displaySuccess, displayError } = useNotification();
  const { mutate: createNewFeature, isLoading: createIsLoading } =
    useCreateAbacAppFeatureMutation(appId);

  //Validation for the create feature form
  const validationSchema = object().shape({
    featureName: string()
      .required("Feature Name is required")
      .trim()
      .max(100, "Feature name must be less than 100 characters"),
    featureDescription: string()
      .trim()
      .max(500, "Feature description must be less than 500 characters"),
    requireAll: bool(),
  });

  //Initial create feature formik setup
  const formik = useFormik({
    initialValues: {
      featureName: "",
      featureDescription: "",
      requireAll: false,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      let newFeature: ICreateFeature = {
        appId: appId,
        name: values.featureName.trim(),
        description: values.featureDescription.trim(),
        requireAll: values.requireAll,
      };
      createFeature(newFeature);
    },
  });

  //Initial create app formik setup
  const handleCreateModalClose = () => {
    if (!createIsLoading) {
      closeModal();
      formik.resetForm();
    } else return;
  };

  // Create a new Feature application
  const createFeature = useCallback(
    function submitFeature(newFeature: ICreateFeature) {
      if (!createIsLoading) {
        createNewFeature(newFeature, {
          onError: () => {
            displayError(createFeatureErrorMessage);
          },
          onSuccess: () => {
            displaySuccess(createFeatureSuccessMessage);
            closeModal();
            formik.resetForm();
          },
        });
      }
    },
    [
      createNewFeature,
      createIsLoading,
      displayError,
      displaySuccess,
      closeModal,
      formik,
    ]
  );

  return {
    getters: {
      createIsLoading,
      formik,
    },
    handlers: { handleCreateModalClose },
  };
}
